import React from 'react';
import { X, History, MoreVertical, Loader2 } from 'lucide-react';
import { formatLocalDateTime } from '../../../../utils/date';

const VersionHistorySidebar = ({
    showVersionSidebar,
    setShowVersionSidebar,
    versionsList,
    activeSection,
    stripPrefix,
    activeVersionMenuId,
    setActiveVersionMenuId,
    handleViewVersion,
    isFetchingVersionDetails,
    setLabelTargetVersion,
    setVersionLabelText,
    setShowLabelModal,
    setRevertTargetVersion,
    setShowRevertModal,
    setIsSelectingCompareFromView,
    isSelectingCompareFromView,
    compareSourceVersion,
    setCompareSourceVersion,
    fetchComparison,
    setSelectedVersionData,
    setShowVersionDetailsModal,
    isFetchingComparison
}) => {
    return (
        <div className={`version-sidebar-overlay ${showVersionSidebar ? 'show' : ''}`} onClick={() => {
            setShowVersionSidebar(false);
            setIsSelectingCompareFromView(false);
            setCompareSourceVersion(null);
        }}>
            <div className={`version-sidebar-content ${showVersionSidebar ? 'show' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div className="version-sidebar-header">
                    {!isSelectingCompareFromView ? (
                        <>
                            <div className="version-title-group">
                                <h4>Version History</h4>
                                <p>{stripPrefix(activeSection)} · {Array.isArray(versionsList) ? versionsList.length : 0} versions</p>
                            </div>
                            <button className="version-close-btn" onClick={() => setShowVersionSidebar(false)}>
                                <X size={20} />
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="version-title-group selection-mode">
                                <h4>Compare with...</h4>
                                <p>Select another version to compare with V{compareSourceVersion?.version_number}</p>
                            </div>
                            <button className="v-cancel-selection-btn" onClick={() => {
                                setIsSelectingCompareFromView(false);
                                setCompareSourceVersion(null);
                            }}>Cancel</button>
                        </>
                    )}
                </div>

                <div className="version-list-container">
                    {!Array.isArray(versionsList) || versionsList.length === 0 ? (
                        <div className="no-versions-state">
                            <History size={32} />
                            <p>No versions available for this section.</p>
                        </div>
                    ) : (() => {
                        const parseUTCDate = (timestamp) => {
                            if (!timestamp) return new Date();
                            let normalized = timestamp;
                            if (typeof timestamp === 'string' && timestamp.includes('T') && !timestamp.endsWith('Z') && !/\+\d{2}:?\d{2}$/.test(timestamp) && !/-\d{2}:?\d{2}$/.test(timestamp)) {
                                normalized = `${timestamp}Z`;
                            }
                            return new Date(normalized);
                        };

                        const groups = {};
                        versionsList.forEach(v => {
                            const date = parseUTCDate(v.created_at);
                            const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                            const today = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                            const yesterday = new Date(Date.now() - 86400000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

                            let label = dateStr;
                            if (dateStr === today) label = 'TODAY';
                            else if (dateStr === yesterday) label = 'YESTERDAY';

                            if (!groups[label]) groups[label] = [];
                            groups[label].push(v);
                        });

                        return Object.entries(groups).map(([label, versions]) => (
                            <div key={label} className="version-date-group">
                                <div className="version-date-label">{label}</div>
                                {versions.map((v, idx) => {
                                    const authorName = v.created_by_name || 'System';
                                    const displayTime = formatLocalDateTime(v.created_at);
                                    const typeLabel = v.change_type_label || 'Edit';
                                    const isSource = compareSourceVersion && (v.version_number === compareSourceVersion.version_number);

                                    return (
                                        <div
                                            key={v.version_id || v.id || idx}
                                            className={`version-item ${idx === 0 && label === 'TODAY' && !isSelectingCompareFromView ? 'active' : ''} ${isSource ? 'source-version' : ''} ${isSelectingCompareFromView ? 'selection-active' : ''}`}
                                            onClick={() => {
                                                if (isSelectingCompareFromView) {
                                                    if (isSource) return;
                                                    fetchComparison(compareSourceVersion.version_number, v.version_number);
                                                    setIsSelectingCompareFromView(false);
                                                    setCompareSourceVersion(null);
                                                } else {
                                                    !isFetchingVersionDetails && handleViewVersion(v);
                                                }
                                            }}
                                            style={{ cursor: (isFetchingVersionDetails || isFetchingComparison) ? 'wait' : 'pointer' }}
                                        >
                                            <div className="version-item-header">
                                                <div className="v-number-row">
                                                    <span className="v-number">{v.label || `Version ${v.version_number}`}</span>
                                                </div>
                                                {!isSelectingCompareFromView && (
                                                    <div className={`v-options-wrapper ${activeVersionMenuId === (v.version_id || v.id || idx) ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                                                        <span className={`v-badge ${(v.change_type || 'manual').toLowerCase()}`}>{typeLabel}</span>
                                                        <button
                                                            className="v-options-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveVersionMenuId(activeVersionMenuId === (v.version_id || v.id || idx) ? null : (v.version_id || v.id || idx));
                                                            }}
                                                        >
                                                            <MoreVertical size={14} />
                                                        </button>
                                                        <div className="v-dropdown-menu">
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); handleViewVersion(v); }} 
                                                                disabled={isFetchingVersionDetails && activeVersionMenuId === (v.version_id || v.id || idx)}
                                                            >
                                                                {isFetchingVersionDetails && activeVersionMenuId === (v.version_id || v.id || idx) ? <Loader2 className="animate-spin" size={12} /> : 'View'}
                                                            </button>
                                                            <button onClick={(e) => {
                                                                e.stopPropagation();
                                                                setLabelTargetVersion(v);
                                                                setVersionLabelText(v.label || '');
                                                                setShowLabelModal(true);
                                                                setActiveVersionMenuId(null);
                                                            }}>Rename</button>
                                                            <button onClick={(e) => {
                                                                e.stopPropagation();
                                                                setRevertTargetVersion(v);
                                                                setShowRevertModal(true);
                                                                setActiveVersionMenuId(null);
                                                            }}>Revert</button>
                                                            <button onClick={(e) => {
                                                                e.stopPropagation();
                                                                setIsSelectingCompareFromView(true);
                                                                setCompareSourceVersion(v);
                                                                setActiveVersionMenuId(null);
                                                            }} disabled={isFetchingComparison}>
                                                                {isFetchingComparison && activeVersionMenuId === (v.version_id || v.id || idx) ? <Loader2 className="animate-spin" size={12} /> : 'Compare'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {isSelectingCompareFromView && isSource && (
                                                    <span className="source-indicator">Selected</span>
                                                )}
                                            </div>
                                            <div className="v-meta">
                                                <div className="v-author-avatar">{authorName[0]}</div>
                                                <span>{authorName} · {displayTime}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ));
                    })()}
                </div>
            </div>
        </div>
    );
};

export default VersionHistorySidebar;
